import { subscribeRaidDomainSignals } from '../../raid-control/api/raidDomainSignals.js';
import { invalidateCurrentUnionRosterCache } from '../../union-roster/api/unionRosterApi.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadRaidTetris, loadRaidTetrisLiveState, loadRaidTetrisPracticeState } from '../api/raidTetrisApi.js';
import { syncTetrisTargetStorage } from '../domain/raidTetrisTargets.js';

const EMPTY = { unionId: '', unionName: '', viewerRole: '', seasons: [], selectedSeason: null };
const LIVE_REFRESH_INTERVAL_MS = 300_000;

export function useRaidTetris() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const activeRef = useRef(true);
  const dataRef = useRef(EMPTY);
  const runRef = useRef(0);
  const selectedSeasonNoRef = useRef(null);
  const requestRef = useRef({ key: '', promise: null });
  const blockingRequestRef = useRef(null);
  // Whether the domain-signal socket is currently carrying changes, which decides if the periodic
  // full reload is still earning its keep.
  const realtimeOpenRef = useRef(false);

  // 'full' reloads the season, 'live' only the canonical attack records, 'practice' only the practice
  // rows. A domain signal says which of those actually changed, so it no longer costs a full reload.
  const refresh = useCallback(async (seasonNo = null, { silent = false, live = false, scope = '' } = {}) => {
    const partial = scope === 'practice' || live;
    if (partial && requestRef.current.promise) {
      await requestRef.current.promise.catch(() => {});
      if (Number(seasonNo) !== selectedSeasonNoRef.current) return null;
    }
    if (!activeRef.current) return null;
    const requestedSeasonNo = seasonNo == null ? null : Number(seasonNo);
    const normalizedSeasonNo = Number.isFinite(requestedSeasonNo) ? requestedSeasonNo : null;
    const requestKey = (normalizedSeasonNo == null ? 'current' : String(normalizedSeasonNo))
      + (scope === 'practice' ? ':practice' : live ? ':live' : ':full');
    const runId = runRef.current + 1;
    runRef.current = runId;

    let requestPromise = requestRef.current.key === requestKey
      ? requestRef.current.promise
      : null;
    if (!requestPromise) {
      const sameSeason = dataRef.current.selectedSeason?.seasonNo === normalizedSeasonNo;
      if (scope === 'practice' && sameSeason) requestPromise = loadRaidTetrisPracticeState(dataRef.current);
      else if (live && sameSeason) requestPromise = loadRaidTetrisLiveState(dataRef.current);
      else requestPromise = loadRaidTetris(normalizedSeasonNo);
      requestRef.current = { key: requestKey, promise: requestPromise };
    }

    if (!silent) {
      blockingRequestRef.current = requestPromise;
      setLoading(true);
    } else if (blockingRequestRef.current) {
      blockingRequestRef.current = requestPromise;
    }

    try {
      const next = await requestPromise;
      if (runRef.current !== runId) return null;
      selectedSeasonNoRef.current = next?.selectedSeason?.seasonNo || null;
      if (typeof window !== 'undefined') {
        syncTetrisTargetStorage({
          storage: window.localStorage,
          unionId: next?.unionId,
          season: next?.selectedSeason,
        });
      }
      dataRef.current = next;
      setData(next);
      setErrorMessage('');
      return next;
    } catch (error) {
      if (runRef.current === runId) setErrorMessage(error instanceof Error ? error.message : '테트리스 데이터를 불러오지 못했습니다.');
      return null;
    } finally {
      if (requestRef.current.promise === requestPromise) requestRef.current = { key: '', promise: null };
      if (blockingRequestRef.current === requestPromise) {
        blockingRequestRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    activeRef.current = true;
    refresh();
    const changed = () => refresh(selectedSeasonNoRef.current, { silent: true });
    let lastResumeAt = 0;
    const refreshWhenVisible = ({ skipWhenLive = false } = {}) => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      // The periodic sweep exists for when the realtime socket is not carrying changes. While it is
      // open every change already arrives as a signal, so reloading the whole season on a timer is
      // the same data twice. Focus and visibility keep refreshing either way: a backgrounded tab can
      // hold a socket that reports open but stopped delivering.
      if (skipWhenLive && realtimeOpenRef.current) return;
      if (Date.now() - lastResumeAt < 1_000) return;
      lastResumeAt = Date.now();
      changed();
    };
    const visibilityChanged = () => {
      if (document.visibilityState === 'visible') refreshWhenVisible();
    };
    const intervalId = window.setInterval(() => refreshWhenVisible({ skipWhenLive: true }), LIVE_REFRESH_INTERVAL_MS);
    window.addEventListener('dororaid:raid-overview-changed', changed);
    window.addEventListener('dororaid:union-roster-changed', changed);
    window.addEventListener('dororaid:raid-season-changed', changed);
    const onFocus = () => refreshWhenVisible();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', visibilityChanged);
    return () => {
      activeRef.current = false;
      runRef.current += 1;
      window.clearInterval(intervalId);
      window.removeEventListener('dororaid:raid-overview-changed', changed);
      window.removeEventListener('dororaid:union-roster-changed', changed);
      window.removeEventListener('dororaid:raid-season-changed', changed);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', visibilityChanged);
    };
  }, [refresh]);

  // Subscribe by identity only; refreshed result objects never recreate the subscription.
  const signalUnionId = data.unionId;
  const signalSeasonId = data.selectedSeason?.id;
  const signalSeasonNo = data.selectedSeason?.seasonNo;
  useEffect(() => {
    if (!signalUnionId || !signalSeasonId) return undefined;
    let active = true, timer = null, running = false, pending = '';
    // A wider scope wins when several signals land in the same window: 'full' covers everything a
    // narrower reload would have fetched.
    const schedule = (kind = 'full') => {
      if (!active) return;
      if (pending !== 'full') pending = pending && pending !== kind ? 'full' : kind;
      if (timer !== null || running || document.visibilityState === 'hidden') return;
      timer = window.setTimeout(async () => {
        timer = null; running = true;
        const scope = pending; pending = '';
        try { await refresh(signalSeasonNo, { silent: true, live: scope === 'live', scope }); }
        finally { running = false; if (pending) schedule(pending); }
      }, 250);
    };
    const unsubscribe = subscribeRaidDomainSignals({
      unionId: signalUnionId, seasonId: signalSeasonId, seasonNo: signalSeasonNo,
      // onStatus hands over { status, ...extra }, not a bare string.
      onStatus: (payload) => { realtimeOpenRef.current = payload?.status === 'open'; },
      onReconnect: () => schedule('full'),
      onChange: event => {
        if (event.domain === 'roster') invalidateCurrentUnionRosterCache();
        if (event.domain === 'results') schedule('live');
        else if (event.domain === 'practice') schedule('practice');
        else if (['roster', 'config'].includes(event.domain)) schedule('full');
      },
    });
    return () => {
      active = false;
      realtimeOpenRef.current = false;
      if (timer !== null) window.clearTimeout(timer);
      unsubscribe();
    };
  }, [signalUnionId, signalSeasonId, signalSeasonNo, refresh]);

  return {
    ...data,
    loading,
    errorMessage,
    refresh,
    selectSeason: (seasonNo) => refresh(Number(seasonNo)),
  };
}
